import React from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'

import Div from '@jarvis.oddle/core/components/Div'
import {
  margin,
  marginSizes,
  padding,
  paddingSizes,
  typo
} from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { selectSections } from 'src/redux/customisation'
import useAppSelector from 'src/hooks/useAppSelector'

// Expose design tokens and theme to CSS variables to use in custom html.
// Because CSS variables are inherit so custom html can have all the variables here as they will render inside it.
const StyledDiv = styled(Div)`
  --smallTextFontSize: ${typo.smallText.mobileFontSize};
  --smallTextLineHeight: ${typo.smallText.mobileLineHeight};
  --pFontSize: ${typo.p.mobileFontSize};
  --pLineHeight: ${typo.p.mobileLineHeight};
  --h1FontSize: ${typo.h1.mobileFontSize};
  --h1LineHeight: ${typo.h1.mobileLineHeight};
  --h2FontSize: ${typo.h2.mobileFontSize};
  --h2LineHeight: ${typo.h2.mobileLineHeight};
  --h3FontSize: ${typo.h3.mobileFontSize};
  --h3LineHeight: ${typo.h3.mobileLineHeight};
  --h4FontSize: ${typo.h4.mobileFontSize};
  --h4LineHeight: ${typo.h4.mobileLineHeight};

  ${breakpoint('desktop')`
  --smallTextFontSize: ${typo.smallText.desktopFontSize};
  --smallTextLineHeight: ${typo.smallText.desktopLineHeight};
  --pFontSize: ${typo.p.desktopFontSize};
  --pLineHeight: ${typo.p.desktopLineHeight};
  --h1FontSize: ${typo.h1.desktopFontSize};
  --h1LineHeight: ${typo.h1.desktopLineHeight};
  --h2FontSize: ${typo.h2.desktopFontSize};
  --h2LineHeight: ${typo.h2.desktopLineHeight};
  --h3FontSize: ${typo.h3.desktopFontSize};
  --h3LineHeight: ${typo.h3.desktopLineHeight};
  --h4FontSize: ${typo.h4.desktopFontSize};
  --h4LineHeight: ${typo.h4.desktopLineHeight};
  `}

  --fontFamily: ${(props) => props.theme.typographyBodyText};
  --fontWeightBold: ${typo.fontWeights.bold};
  --fontWeightRegular: ${typo.fontWeights.regular};
  --paddingExtraSmall: ${padding[paddingSizes.extraSmall]};
  --paddingSmall: ${padding[paddingSizes.small]};
  --paddingMedium: ${padding[paddingSizes.medium]};
  --paddingLarge: ${padding[paddingSizes.large]};
  --paddingExtraLarge: ${padding[paddingSizes.extraLarge]};
  --marginExtraSmall: ${margin[marginSizes.extraSmall]};
  --marginSmall: ${margin[marginSizes.small]};
  --marginMedium: ${margin[marginSizes.medium]};
  --marginLarge: ${margin[marginSizes.large]};
  --marginExtraLarge: ${margin[marginSizes.extraLarge]};
  --colorSectionHeadingsFont: ${(props) =>
    props.theme.colorSectionHeadingsFont};
  --colorHeadingsFont: ${(props) => props.theme.colorHeadingsFont};
  --colorBodyFont: ${(props) => props.theme.colorBodyFont};
  --colorLinesAndBorders: ${(props) => props.theme.colorLinesAndBorders};
  --colorContainerColor: ${(props) => props.theme.colorContainerColor};
  --colorBackgroundColor: ${(props) => props.theme.colorBackgroundColor};
  --buttonTextColor: ${(props) => props.theme.buttonTextColor};
  --buttonColor: ${(props) => props.theme.buttonColor};
`

interface CustomHtmlSection {
  id: string
  name: string
  type: string
  settings: {
    htmlContent: string
  }
  blocks?: {
    [key: string]: unknown
  }
  blocksOrder?: string[]
  updatedAt: string
  options: {
    visibleForReview: boolean
  }
}

interface CustomHtmlProps {
  id: string
}

const CustomHtml = ({ id }: CustomHtmlProps): JSX.Element => {
  const router = useRouter()
  const { settings } = useAppSelector(selectSections)[id] as CustomHtmlSection
  const { htmlContent } = settings
  let __html = htmlContent
  if (htmlContent.includes('https://reserve.oddle.me/js/widget.js?')) {
    __html = htmlContent.replace(
      'https://reserve.oddle.me/js/widget.js?',
      `https://reserve.oddle.me/js/widget.js?lang=${router.query.locale}&`
    )
  }

  React.useEffect(() => {
    const existElement = document.getElementById(id)
    const script = document.createRange().createContextualFragment(__html)
    existElement.appendChild(script)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <StyledDiv id={id} />
}

export default CustomHtml
